import { useEvents } from "api/events";
import { memo } from "react";
import {
  CampaignTriggers,
  CampaignTriggersUnion,
  DirectCampaignRule,
  SelectedSchemaCondition,
  TransactionItem,
  TriggerStrategy,
  TriggerStrategyType,
} from "types/campaigns";
import { findConditionIdPath } from "../../campaignHelpers";
import {
  achievementEventDirectCampaignConditions,
  directCustomerConditions,
  eventDirectCampaignConditions,
  transactionDirectCampaignConditions,
} from "../rules";
import {
  filterOldTierConditions,
  generateSchemaConditions,
} from "../rulesHelpers";
import { DirectCampaignRules } from "./DirectCampaignRules";

interface Props {
  rules: DirectCampaignRule[];
  trigger: CampaignTriggersUnion;
  event?: string;
  transactionItemsFilters?: TransactionItem[];
  errors: any;
  triggerStrategy?: TriggerStrategy;
  isEditMode: boolean;
}

const selectConditions = (
  trigger: CampaignTriggersUnion,
  event?: string,
  triggerStrategy?: TriggerStrategy,
) => {
  switch (trigger) {
    case CampaignTriggers.TRANSACTION:
    case CampaignTriggers.RETURN_TRANSACTION:
      return transactionDirectCampaignConditions;
    case CampaignTriggers.INTERNAL_EVENT:
      return event === "MemberAchievementProgressWasChanged"
        ? achievementEventDirectCampaignConditions
        : eventDirectCampaignConditions;
    case CampaignTriggers.CUSTOM_EVENT:
      return eventDirectCampaignConditions;
    case CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE:
      return eventDirectCampaignConditions;
    case CampaignTriggers.ACHIEVEMENT:
      return directCustomerConditions;
    case CampaignTriggers.TIME:
      const isTriggerStrategyDaily =
        triggerStrategy?.type === TriggerStrategyType.DAILY;
      const forbiddenConditions = ["customer.segments", "customer.levelId"];

      return isTriggerStrategyDaily
        ? directCustomerConditions.filter(
            (condition) =>
              !forbiddenConditions.includes(findConditionIdPath(condition)),
          )
        : directCustomerConditions;
    default:
      return [];
  }
};

export const DirectCampaign = memo(
  ({
    rules,
    trigger,
    event,
    transactionItemsFilters,
    errors,
    triggerStrategy,
    isEditMode,
  }: Props) => {
    const triggerConditions = selectConditions(trigger, event, triggerStrategy);
    const { useCustomEventSchemaDetails } = useEvents();

    const { data: selectedSchemaDetails } = useCustomEventSchemaDetails(
      trigger !== CampaignTriggers.INTERNAL_EVENT ? event : undefined,
    );

    const selectedSchemaConditions: SelectedSchemaCondition[] =
      generateSchemaConditions(selectedSchemaDetails?.schema?.fields);
    const conditions = [
      ...triggerConditions,
      ...(trigger === CampaignTriggers.CUSTOM_EVENT
        ? selectedSchemaConditions
        : []),
    ];

    const filteredConditions = filterOldTierConditions({
      conditions,
      isEditMode,
      rules,
    });

    return (
      <DirectCampaignRules
        conditions={filteredConditions || []}
        selectedSchemaConditions={selectedSchemaConditions}
        errors={errors}
        rules={rules}
        trigger={trigger}
        transactionItemsFilters={transactionItemsFilters}
      />
    );
  },
);
